import { all, call, put, race, select, take, takeEvery } from 'redux-saga/effects'
import { waitForChange } from 'gnos-utilities/sagas'
import { List, Map } from 'immutable'
import { SUCCESS, FAILED, REQUEST } from 'crom-api-constants'
import { dataTableForceReload, dataTableRemoveData } from 'gnos-tables'
import { barRemoveData as barNestedCategoriesChartRemoveData } from 'gnos-charts'
import _ from 'lodash'

import config from '@config'

import IntlSaga from '../../../../services/translations/intl-saga'

import portfolioRibbonSaga from './components/Ribbon/saga'
import portfolioToolbarSagas from './components/Toolbar/sagas'

import {
    localeSelector,
    accessRightsLoadedSelector,
    activeGroupPortfolioId,
    getPortfolioBlocksById,
    getRibbonActiveBlocks,
    getRibbonActivePortfolioType,
    getToggleBlocksById,
    hasAccessRightsToPortfolio,
    getDataTableIds,
    isPortfolioOpen,
    getPortfolioDataTablesIds,
    areAllBlockLoaded,
    getBlockLevels,
    getCountOfMembers,
    getBarNestedCategoriesChartIds,
    getAccessRightsPortfolios,
} from './selectors'

import {
    PORTFOLIO_ACCESS_RIGHTS_REQUEST,
    LOAD_PORTFOLIO_MODULE_ACCESS_RIGHTS,
    OPEN_PORTFOLIO_BY_ID,
    CLOSE_PORTFOLIO_BY_ID,
    REFRESH_PORTFOLIO_BY_ID,
    PORTFOLIO_GET_GROUP_INFO_REQUEST,
    PORTFOLIO_GET_TOKEN_REQUEST,
    LOCALE_PREFIX,
    PORTFOLIO_LOADER_ID,
    PORTFOLIO_GROUP_LOADER_ID,
    DELETED_GROUP_MEMBER_COUNT,
} from './constants'
import { TABS_NEW_TAB_ADDED, TABS_TAB_REMOVED } from '../../components/Tabs/constants'
import { PORTFOLIO_RIBBON_CHANGE_EVENT, BUTTONSARRAY_KEY } from './components/Ribbon/constants'
import {
    BLOCK_DATA_START_LOADING,
    BLOCK_DATA_LOADED,
    BLOCK_DATA_LOAD_FAILED,
} from '../../components/Workspace/components/HocBlock/constants'
import { CONFIRM_DIALOG_ON_BUTTON_CLICK } from '../../../../components/Dialog/components/Confirm/constants'

import {
    portfolioAccessRightsRequest,
    setPortfolioModuleAccessRights,
    portfolioGetGroupInfoRequest,
    portfolioGetTokenRequest,
    portfolioGroupUpdate,
    portfolioGroupDeleteUpdate,
    closePortfolioById,
    refreshPortfolioById,
    openPortfolioById,
} from './actions'
import { addTab, setTabText, removeTab, activeTab } from '../../components/Tabs/actions'
import { showLoader, hideLoader } from '../../../../services/loader/actions'
import { openDialog } from '../../../../services/dialog/actions'
import { loadBlockData } from '../../components/Workspace/components/HocBlock/actions'
import { sendMessage } from '../../../../services/socket/helpers'
import { portfolioTypeBlocksSelector } from './components/Ribbon/selectors'
import { changebuttonsArrayButton } from '../../components/Ribbon/components/ButtonsArray/components/ConnectButton/actions'
import { getAppState } from '../../../../services/appState/selectors'
import { activeItem } from '../../components/Ribbon/components/DropDown/actions'

const MODULE_KEY = config.modules.portfolio.key

const PORTFOLIO_ACCESS_RIGHTS_REQUEST_SUCCESS = `${PORTFOLIO_ACCESS_RIGHTS_REQUEST}_${SUCCESS}`
const PORTFOLIO_ACCESS_RIGHTS_REQUEST_FAILED = `${PORTFOLIO_ACCESS_RIGHTS_REQUEST}_${FAILED}`

const PORTFOLIO_GET_GROUP_INFO_REQUEST_SUCCESS = (portfolioId) =>
    `${PORTFOLIO_GET_GROUP_INFO_REQUEST(portfolioId)}_${SUCCESS}`
const PORTFOLIO_GET_GROUP_INFO_REQUEST_FAILED = (portfolioId) =>
    `${PORTFOLIO_GET_GROUP_INFO_REQUEST(portfolioId)}_${FAILED}`
const PORTFOLIO_GET_TOKEN_REQUEST_SUCCESS = (portfolioId) => `${PORTFOLIO_GET_TOKEN_REQUEST(portfolioId)}_${SUCCESS}`
const PORTFOLIO_GET_TOKEN_REQUEST_FAILED = (portfolioId) => `${PORTFOLIO_GET_TOKEN_REQUEST(portfolioId)}_${FAILED}`

const portfolioRequestFailedRegex = new RegExp(
    `^(DATA_TABLE_DATA|LOAD_BLOCK)_${_.toUpper(MODULE_KEY)}_.*_(DATA_TABLE|DATA)_${REQUEST}_${FAILED}$`
)

function isPortfolioAdd(_action) {
    const actionType = _.get(_action, 'type', '')
    const moduleType = _.get(_action, 'data.type', '')

    return actionType === TABS_NEW_TAB_ADDED && moduleType === MODULE_KEY
}

function isPortfolioRemove(_action) {
    const actionType = _.get(_action, 'type', '')
    const moduleType = _.get(_action, 'data.type', '')

    return actionType === TABS_TAB_REMOVED && moduleType === MODULE_KEY
}

function isBlockOrDataTableRequestFailed(_action) {
    const actionType = _.get(_action, 'type', '')

    return portfolioRequestFailedRegex.test(actionType)
}

function isClosingPortfolio(id) {
    return (_action) => _.get(_action, 'type', null) === CLOSE_PORTFOLIO_BY_ID && _.get(_action, 'data.id', null) === id
}

function* setPortfolioAppStateSaga() {
    // Initial load of active ribbon blocks from app-state
    const appState = yield select(getAppState)
    if (appState) {
        const portfolios = yield select(getAccessRightsPortfolios)
        // Set portfolio ribbon type from app-state, if the type is in access rights
        if (appState.portfolio?.activeType && portfolios.get(appState.portfolio?.activeType)) {
            const activeType = appState.portfolio?.activeType

            yield put(activeItem(MODULE_KEY, 'portfolios', activeType))
        }

        // Set active ribbon buttons from app-state
        if (!_.isEmpty(appState.portfolio?.ribbonActiveBlocks)) {
            const ribbonActiveBlocks = appState.portfolio.ribbonActiveBlocks
            const portfolioBlocks = yield select(portfolioTypeBlocksSelector)

            const changeActions = portfolioBlocks
                .map((block) =>
                    put(changebuttonsArrayButton(MODULE_KEY, BUTTONSARRAY_KEY, block, ribbonActiveBlocks?.[block]))
                )
                .toJS()

            yield all(changeActions)
        }
    }

    return null
}

function* loadPortfolioAccessRights() {
    const isLoaded = yield select(accessRightsLoadedSelector)

    if (isLoaded) return null

    const locale = yield select(localeSelector)

    yield put(showLoader(PORTFOLIO_LOADER_ID, IntlSaga.formatMessage(locale, `${LOCALE_PREFIX}.loadingText`)))

    const portfolioAccessRights = yield all({
        response: race({
            success: take(PORTFOLIO_ACCESS_RIGHTS_REQUEST_SUCCESS),
            failed: take(PORTFOLIO_ACCESS_RIGHTS_REQUEST_FAILED),
        }),
        request: put(portfolioAccessRightsRequest()),
    })
    const portfolioAccessRightsData = _.get(portfolioAccessRights, 'response.success.payload.response.body.data', {})

    // TODO TEST OF DISABLED BUTTON - if there is admin type, add test banner button
    if (_.get(portfolioAccessRightsData, 'types.admin', null)) {
        yield put(
            setPortfolioModuleAccessRights(
                _.set(_.cloneDeep(portfolioAccessRightsData), 'types.admin.blocks', [
                    ..._.get(portfolioAccessRightsData, 'types.admin.blocks', {}),
                    { name: 'banner', order: 500, isDisabled: true, defaultLevel: 0, availableLevels: [] },
                ])
            )
        )
    } else {
        yield put(setPortfolioModuleAccessRights(portfolioAccessRightsData))
    }
    // TODO END OF TEST OF DISABLED BUTTON

    yield setPortfolioAppStateSaga()

    yield put(hideLoader(PORTFOLIO_LOADER_ID))

    return null
}

function* loadActiveBlocks(portfolioId, refresh = false) {
    const locale = yield select(localeSelector)
    yield put(
        showLoader(PORTFOLIO_GROUP_LOADER_ID, IntlSaga.formatMessage(locale, `${LOCALE_PREFIX}.loadingGroupInfo`))
    )

    // load and add portfolio to collection
    const result = yield all({
        response: race({
            success: take(PORTFOLIO_GET_TOKEN_REQUEST_SUCCESS(portfolioId)),
            failed: take(PORTFOLIO_GET_TOKEN_REQUEST_FAILED(portfolioId)),
        }),
        request: put(portfolioGetTokenRequest(portfolioId)),
    })

    yield put(hideLoader(PORTFOLIO_GROUP_LOADER_ID))

    if (result.response.failed) {
        const errorCode = _.get(result, 'response.failed.payload.error.code', 500)

        if (errorCode === 403) {
            const dialogSettings = {
                header: { textId: 'portfolio', icon: '' },
                question: { textId: 'portfolioOverdrawn' },
                buttons: ['ok'],
            }

            yield all({
                answer: take(CONFIRM_DIALOG_ON_BUTTON_CLICK('ok', portfolioId)),
                dialog: put(openDialog('confirm', portfolioId, dialogSettings)),
            })
        }

        // if token error, close portfolio
        yield put(closePortfolioById(portfolioId))

        return null
    }

    if (refresh) {
        // reload active blocks
        const activeBlocks = yield select(getPortfolioBlocksById, portfolioId)
        const blockLevels = yield select(getBlockLevels, portfolioId, activeBlocks)
        const reloadBlockActions = activeBlocks
            .map((block) => put(loadBlockData(MODULE_KEY, portfolioId, block, blockLevels.get(block))))
            .toJS()
        yield all(reloadBlockActions)

        // reload all tables in active blocks
        const activeDataTableIds = yield select(getPortfolioDataTablesIds, portfolioId)
        const reloadDataTableActions = activeDataTableIds.map((tableId) => put(dataTableForceReload(tableId))).toJS()
        yield all(reloadDataTableActions)

        // delete data for tables in inactive blocks (must be before delete inactive blocks)
        const inactiveDataTableIds = yield select(getPortfolioDataTablesIds, portfolioId, false)
        const deleteDataTableActions = inactiveDataTableIds.map((tableId) => put(dataTableRemoveData(tableId))).toJS()
        yield all(deleteDataTableActions)

        // delete data for inactive blocks
        const inactiveBlocks = yield select(getPortfolioBlocksById, portfolioId, false)
        const deleteUpdates = inactiveBlocks.map((blockKey) => ['blocks', blockKey])
        yield put(portfolioGroupDeleteUpdate(portfolioId, deleteUpdates))
    } else {
        yield put(
            showLoader(PORTFOLIO_GROUP_LOADER_ID, IntlSaga.formatMessage(locale, `${LOCALE_PREFIX}.loadingGroupInfo`))
        )

        const activeBlocks = yield select(getRibbonActiveBlocks)
        const activePortfolioType = yield select(getRibbonActivePortfolioType)

        // create updates for portfolio
        const updates = Map({
            blocks: activeBlocks.reduce((acc, blockKey) => acc.set(blockKey, Map({ active: true })), Map()),
            portfolioType: activePortfolioType,
        })

        yield put(portfolioGroupUpdate(portfolioId, updates))

        // load active blocks
        const blockLevels = yield select(getBlockLevels, portfolioId, activeBlocks)
        const loadBlockActions = activeBlocks
            .map((block) => put(loadBlockData(MODULE_KEY, portfolioId, block, blockLevels.get(block))))
            .toJS()
        yield all(loadBlockActions)

        // hide loader when no block (in ribbon) is selected
        if (activeBlocks.isEmpty()) yield put(hideLoader(PORTFOLIO_GROUP_LOADER_ID))
    }

    return null
}

function* addPortfolio(_action) {
    const portfolioId = _.get(_action, 'data.id', null)

    // load token and start load blocks data
    yield loadActiveBlocks(portfolioId)

    return null
}

function* removePortfolio(_action) {
    const portfolioId = _.get(_action, 'data.id', null)

    // remove portfolio from store and clear data tables, (removing from tab)
    yield put(closePortfolioById(portfolioId, true))

    return null
}

function* onRibbonButtonsChange() {
    const activePortfolioId = yield select(activeGroupPortfolioId)
    if (!activePortfolioId) return null

    // create updates for portfolio
    const toggleBlocks = yield select(getToggleBlocksById, activePortfolioId)
    const toActiveUpdates = toggleBlocks
        .get('toActive', List())
        .reduce((acc, blockKey) => acc.set(blockKey, Map({ active: true })), Map())
    const toInactiveUpdates = toggleBlocks
        .get('toInactive', List())
        .reduce((acc, blockKey) => acc.set(blockKey, Map({ active: false })), Map())
    const activePortfolioType = yield select(getRibbonActivePortfolioType)

    const updates = Map({
        blocks: toActiveUpdates.mergeDeep(toInactiveUpdates),
        portfolioType: activePortfolioType,
    })

    yield put(portfolioGroupUpdate(activePortfolioId, updates))

    return null
}

function* onBlockDataStartLoading(_action) {
    const module = _.get(_action, 'data.module', null)
    if (module !== MODULE_KEY) return null

    const portfolioId = _.get(_action, 'data.id', null)
    const blockKey = _.get(_action, 'data.blockKey', null)
    if (!portfolioId || !blockKey) return null

    // create update for portfolio
    const updates = Map({ blocks: Map({ [blockKey]: Map({ isLoading: true }) }) })

    yield put(portfolioGroupUpdate(portfolioId, updates))

    return null
}

function* onBlockDataLoaded(_action) {
    const module = _.get(_action, 'data.module', null)
    if (module !== MODULE_KEY) return null

    const portfolioId = _.get(_action, 'data.id', null)
    const blockKey = _.get(_action, 'data.blockKey', null)
    const blockData = _.get(_action, 'data.data', null)
    const locale = _.get(_action, 'data.locale', null)
    if (!portfolioId || !blockKey) return null

    // create update for portfolio
    const updates = Map({
        blocks: Map({
            [blockKey]: Map({
                isLoading: false,
                isLoaded: true,
                loadedLocale: locale,
                data: blockData,
            }),
        }),
    })

    yield put(portfolioGroupUpdate(portfolioId, updates))

    const isAllLoaded = yield select(areAllBlockLoaded, portfolioId)
    if (isAllLoaded) yield put(hideLoader(PORTFOLIO_GROUP_LOADER_ID))

    return null
}

function* onBlockDataLoadFailed(_action) {
    const module = _.get(_action, 'data.module', null)
    if (module !== MODULE_KEY) return null

    const portfolioId = _.get(_action, 'data.id', null)
    const blockKey = _.get(_action, 'data.blockKey', null)
    if (!portfolioId || !blockKey) return null

    // create update for portfolio
    const updates = Map({
        blocks: Map({
            [blockKey]: Map({
                isLoading: false,
                isLoaded: true,
            }),
        }),
    })

    yield put(portfolioGroupUpdate(portfolioId, updates))

    const isAllLoaded = yield select(areAllBlockLoaded, portfolioId)
    if (isAllLoaded) yield put(hideLoader(PORTFOLIO_GROUP_LOADER_ID))

    return null
}

function* onBlockOrDataTableRequestFailed(_action) {
    const errorCode = _.get(_action, 'payload.error.code', null)
    const portfolioId = _.get(_action, 'payload.originAction.payload.qs.groupId', null)

    if (!portfolioId) return null

    if (errorCode === 403) {
        yield put(refreshPortfolioById(portfolioId))
    }

    if (errorCode === 404) {
        const dialogSettings = {
            header: { textId: 'portfolio', icon: '' },
            question: { textId: 'portfolioDeleted' },
            buttons: ['ok'],
        }

        yield all({
            answer: take(CONFIRM_DIALOG_ON_BUTTON_CLICK('ok', portfolioId)),
            dialog: put(openDialog('confirm', portfolioId, dialogSettings)),
        })

        yield put(closePortfolioById(portfolioId))
    }

    return null
}

function* confirmDialog(portfolioId, refresh = false, noShowWhenZeroToPay = false) {
    const locale = yield select(localeSelector)
    yield put(
        showLoader(PORTFOLIO_GROUP_LOADER_ID, IntlSaga.formatMessage(locale, `${LOCALE_PREFIX}.loadingGroupInfo`))
    )

    const groupInfo = yield all({
        response: race({
            success: take(PORTFOLIO_GET_GROUP_INFO_REQUEST_SUCCESS(portfolioId)),
            failed: take(PORTFOLIO_GET_GROUP_INFO_REQUEST_FAILED(portfolioId)),
        }),
        request: put(portfolioGetGroupInfoRequest(portfolioId)),
    })

    yield put(hideLoader(PORTFOLIO_GROUP_LOADER_ID))

    if (groupInfo.response.failed) return { cancel: true }
    const groupInfoData = _.get(groupInfo, 'response.success.payload.response.body.data', {})

    if (noShowWhenZeroToPay && _.get(groupInfoData, 'counts.toPay', null) === 0) {
        return { show: true }
    }

    const dialogSettings = {
        header: { textId: 'portfolioName', icon: '', values: { name: groupInfoData.name } },
        question: {
            textId: refresh ? 'portfolioTokenRefresh' : 'portfolio',
            values: {
                ...groupInfoData.counts,
                name: groupInfoData.name,
            },
        },
        buttons: ['show', 'cancel'],
    }

    const userResponse = yield all({
        answer: race({
            show: take(CONFIRM_DIALOG_ON_BUTTON_CLICK('show', portfolioId)),
            cancel: take(CONFIRM_DIALOG_ON_BUTTON_CLICK('cancel', portfolioId)),
        }),
        dialog: put(openDialog('confirm', portfolioId, dialogSettings)),
    })

    return userResponse.answer
}

function* onOpenPortfolioById(_action) {
    const portfolioId = _.get(_action, 'data.id', null)
    if (!portfolioId) return null

    const moduleEnabled = yield select(hasAccessRightsToPortfolio)
    if (!moduleEnabled) return null

    // Check if the entity has been already opened for this module
    const isOpen = yield select(isPortfolioOpen, portfolioId)
    if (isOpen) {
        // Open the tab with the entity (doesn't reload the whole workspace again)
        yield put(activeTab(portfolioId, MODULE_KEY))
        yield put(refreshPortfolioById(portfolioId, true))

        return null
    }

    const userAnswer = yield confirmDialog(portfolioId)

    if (userAnswer.cancel) return null

    yield put(addTab(portfolioId, MODULE_KEY, ''))

    const result = yield race({
        success: take(PORTFOLIO_GET_TOKEN_REQUEST_SUCCESS(portfolioId)),
        failed: take(PORTFOLIO_GET_TOKEN_REQUEST_FAILED(portfolioId)),
    })

    if (result.failed) return null

    const name = _.get(result.success, 'payload.response.body.data.group.name', '')

    yield put(setTabText(portfolioId, MODULE_KEY, name))
    sendMessage('addTabFromNew', { entId: portfolioId, module: MODULE_KEY, name })

    // wait for change of group members count or portfolio close
    const observer = yield race({
        change: call(waitForChange, getCountOfMembers, portfolioId),
        close: take(isClosingPortfolio(portfolioId)),
    })

    // when change in store, close portfolio and show dialog
    if (_.isFinite(observer.change)) {
        yield put(closePortfolioById(portfolioId))

        const isGroupDeleted = observer.change === DELETED_GROUP_MEMBER_COUNT
        const dialogSettings = {
            header: { textId: 'portfolio', icon: '' },
            question: {
                textId: isGroupDeleted ? 'portfolioGroupDeleted' : 'portfolioMemberCountChanged',
                values: { name },
            },
            buttons: isGroupDeleted ? ['ok'] : ['againOpen', 'ok'],
        }

        const userResponse = yield all({
            answer: race({
                againOpen: take(CONFIRM_DIALOG_ON_BUTTON_CLICK('againOpen', portfolioId)),
                ok: take(CONFIRM_DIALOG_ON_BUTTON_CLICK('ok', portfolioId)),
            }),
            dialog: put(openDialog('confirm', portfolioId, dialogSettings)),
        })

        if (userResponse.answer.againOpen) yield put(openPortfolioById(portfolioId))
    }

    return null
}

function* onClosePortfolioById(_action) {
    const portfolioId = _.get(_action, 'data.id', null)
    if (!portfolioId) return null

    const isCloseFromTab = _.get(_action, 'data.fromTab', false)

    // remove data of data tables of block
    const dataTableIds = yield select(getDataTableIds, portfolioId)
    const dataTableRemoveDataActions = dataTableIds.map((tableId) => put(dataTableRemoveData(tableId))).toJS()
    yield all(dataTableRemoveDataActions)

    // remove data of barNestedCategories charts
    const chartsIds = yield select(getBarNestedCategoriesChartIds, portfolioId)
    const barNestedCategoriesChartRemoveDataActions = chartsIds
        .map((chartId) => put(barNestedCategoriesChartRemoveData(chartId)))
        .toJS()
    yield all(barNestedCategoriesChartRemoveDataActions)

    // if portfolio is closing from tab, don't remove tab
    if (!isCloseFromTab) yield put(removeTab(portfolioId, MODULE_KEY))

    return null
}

function* onRefreshPortfolioById(_action) {
    const portfolioId = _.get(_action, 'data.id', null)
    if (!portfolioId) return null

    const moduleEnabled = yield select(hasAccessRightsToPortfolio)
    if (!moduleEnabled) return null

    const isOpen = yield select(isPortfolioOpen, portfolioId)
    if (isOpen) {
        const forceRefresh = _.get(_action, 'data.force', false)
        const userAnswer = yield confirmDialog(portfolioId, true, forceRefresh)

        if (userAnswer.cancel) {
            yield put(closePortfolioById(portfolioId))

            return null
        }

        // reload token and merge new portfolio information to collection
        yield loadActiveBlocks(portfolioId, true)
    }

    return null
}

export default function* portfolioSaga() {
    yield all([
        takeEvery(LOAD_PORTFOLIO_MODULE_ACCESS_RIGHTS, loadPortfolioAccessRights),
        takeEvery(isPortfolioAdd, addPortfolio),
        takeEvery(isPortfolioRemove, removePortfolio),
        takeEvery(PORTFOLIO_RIBBON_CHANGE_EVENT, onRibbonButtonsChange),
        takeEvery(BLOCK_DATA_START_LOADING, onBlockDataStartLoading),
        takeEvery(BLOCK_DATA_LOADED, onBlockDataLoaded),
        takeEvery(BLOCK_DATA_LOAD_FAILED, onBlockDataLoadFailed),
        takeEvery(isBlockOrDataTableRequestFailed, onBlockOrDataTableRequestFailed),
        takeEvery(OPEN_PORTFOLIO_BY_ID, onOpenPortfolioById),
        takeEvery(CLOSE_PORTFOLIO_BY_ID, onClosePortfolioById),
        takeEvery(REFRESH_PORTFOLIO_BY_ID, onRefreshPortfolioById),
        portfolioRibbonSaga(),
        portfolioToolbarSagas(),
    ])
}
